import { Component, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface Props {
  children: ReactNode;
  /** A short label for telemetry — e.g. "CrossAuditsPage" or "AppShell". */
  scope?: string;
  /** When set, render this instead of the default fallback. */
  fallback?: (err: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

/** Catches runtime exceptions thrown by any descendant during render,
 * lifecycle, or constructor calls. Replaces the white-screen-of-death
 * with a recoverable card that lets the user reload, copy the error,
 * or fall back to the dashboard.
 *
 * Wrap routes (not just the whole app) so a crash on one screen
 * doesn't take down navigation — the AppShell stays mounted. */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    const { scope } = this.props;
    // Log for the browser console; ops can fish this out of session
    // replay. We deliberately don't POST to the backend — page-crash
    // telemetry isn't worth the moving part for the dashboard's size.
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary${scope ? `:${scope}` : ''}]`, error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error == null) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <Card sx={{ borderTop: '4px solid #DC2626' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 36 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  קרתה תקלה בטעינת הדף
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  המסך הזה התרסק במהלך הטעינה. אפשר לנסות לרענן או לחזור לדאשבורד הראשי.
                </Typography>
              </Box>
            </Stack>
            <Box
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: 12,
                bgcolor: '#FEF2F2',
                color: '#7F1D1D',
                p: 1.5,
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 220,
                direction: 'ltr',
                textAlign: 'left',
                whiteSpace: 'pre-wrap',
              }}
            >
              {this.state.error.name}: {this.state.error.message}
              {this.state.error.stack && '\n\n' + this.state.error.stack.split('\n').slice(0, 8).join('\n')}
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => window.location.reload()}>
                רענן דף
              </Button>
              <Button variant="outlined" onClick={() => { window.location.href = '/dashboard'; }}>
                לדאשבורד הראשי
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                size="small"
                onClick={() => {
                  const err = this.state.error;
                  if (!err) return;
                  const text = `${err.name}: ${err.message}\n${err.stack ?? ''}`;
                  navigator.clipboard.writeText(text).catch(() => { /* ignore */ });
                }}
              >
                העתק שגיאה
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }
}
